import { useCallback, useEffect, useRef } from "react";

/** Schedule logout exactly when JWT expires (absolute timeout). */
export function useAbsoluteTokenTimer(token, onExpire) {
    useEffect(() => {
        if (!token) return;

        let cancel = () => {};
        try {
            const [, payload] = token.split(".");
            const data = JSON.parse(atob(payload));
            const msLeft = data.exp * 1000 - Date.now();
            if (msLeft <= 0) {
                onExpire?.("expired");
                return;
            }
            const id = setTimeout(() => onExpire?.("expired"), msLeft);
            cancel = () => clearTimeout(id);
        } catch {
            onExpire?.("expired");
        }
        return cancel;
    }, [token, onExpire]);
}

/**
 * Log out on inactivity. Works across tabs.
 * - idleMs: e.g. 15 * 60 * 1000 (15 min)
 */
export function useIdleLogout(
    onIdle,
    idleMs = 15 * 60 * 1000,
    key = "lastActivityAt",
    enabled = true,
    suppressActivity = false
) {
    const timerRef = useRef();
    const resetRef = useRef(() => {});

    useEffect(() => {
        if (!enabled) {
            resetRef.current = () => {};
            return;
        }
        const readLastActivity = () => {
            const raw = localStorage.getItem(key);
            const val = Number(raw);
            return Number.isFinite(val) && val > 0 ? val : null;
        };

        const scheduleFromLast = (lastActivity) => {
            clearTimeout(timerRef.current);
            const elapsed = Date.now() - lastActivity;
            const msLeft = idleMs - elapsed;
            if (msLeft <= 0) {
                onIdle?.("idle");
                return;
            }
            timerRef.current = setTimeout(() => onIdle?.("idle"), msLeft);
        };

        const reset = () => {
            const now = Date.now();
            localStorage.setItem(key, String(now));
            scheduleFromLast(now);
        };
        const handleActivity = () => {
            if (suppressActivity) return;
            reset();
        };
        resetRef.current = reset;

        // start + listen to user activity
        const events = [
            "mousemove",
            "keydown",
            "click",
            "scroll",
            "touchstart",
        ];
        events.forEach((e) =>
            window.addEventListener(e, handleActivity, { passive: true })
        );
        if (suppressActivity) {
            const last = readLastActivity() ?? Date.now();
            scheduleFromLast(last);
        } else {
            reset();
        }

        // cross-tab sync
        const onStorage = (e) => {
            if (e.key === key) {
                const last = Number(e.newValue || 0) || Date.now();
                scheduleFromLast(last);
            }
        };
        window.addEventListener("storage", onStorage);

        return () => {
            events.forEach((e) => window.removeEventListener(e, handleActivity));
            window.removeEventListener("storage", onStorage);
            clearTimeout(timerRef.current);
            resetRef.current = () => {};
        };
    }, [idleMs, key, onIdle, enabled, suppressActivity]);

    return useCallback(() => resetRef.current?.(), []);
}
